import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Globe, Instagram, Pin, Info, Facebook, Youtube, Music2 } from "lucide-react";
import { useAuth, getToken } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";

export function SocialSettings() {
    const { user, refreshAuth } = useAuth();
    const { toast } = useToast();
    const { t } = useI18n();
    const queryClient = useQueryClient();

    // Fetch full profile data
    const { data: profile, isLoading } = useQuery<any>({
        queryKey: ["/api/profiles/me"],
        enabled: !!user,
    });

    const [formData, setFormData] = useState({
        name: "",
        location: "",
        styleText: "",
        lookingForText: "",
        isWishlistPublic: false,
        socialLinks: {
            website: "",
            instagram: "",
            pinterest: "",
            facebook: "",
            youtube: "",
            tiktok: ""
        }
    });

    useEffect(() => {
        if (profile) {
            setFormData({
                name: profile.name || "",
                location: profile.location || "",
                styleText: profile.style_text || "",
                lookingForText: profile.looking_for_text || "",
                isWishlistPublic: profile.is_wishlist_public ?? false,
                socialLinks: {
                    website: profile.social_links?.website || "",
                    instagram: profile.social_links?.instagram || "",
                    pinterest: profile.social_links?.pinterest || "",
                    facebook: profile.social_links?.facebook || "",
                    youtube: profile.social_links?.youtube || "",
                    tiktok: profile.social_links?.tiktok || ""
                }
            });
        }
    }, [profile]);

    const mutation = useMutation({
        mutationFn: async (updatedData: any) => {
            const token = await getToken();
            const res = await fetch("/api/profiles/me", {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(updatedData)
            });
            if (!res.ok) throw new Error("Update failed");
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/profiles/me"] });
            refreshAuth?.();
            toast({
                title: "Profiel bijgewerkt",
                description: "Je sociale profiel is succesvol opgeslagen."
            });
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: "Fout bij opslaan",
                description: "Er ging iets mis bij het bijwerken van je profiel."
            });
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Map to snake_case for backend
        const payload = {
            name: formData.name,
            location: formData.location,
            style_text: formData.styleText,
            looking_for_text: formData.lookingForText,
            is_wishlist_public: formData.isWishlistPublic,
            social_links: formData.socialLinks
        };
        mutation.mutate(payload);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t("account.tabs.profile")}</CardTitle>
                <CardDescription>
                    Personaliseer je openbare profiel voor de VintMap community.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="name">{t("social.settings.fullName")}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Bijv. Wilma Boekholt"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="location">{t("social.settings.location")}</Label>
                            <Input
                                id="location"
                                value={formData.location}
                                onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                                placeholder="Bijv. Amsterdam / Gelderland"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="styleText">{t("social.style.bio")}</Label>
                        <Textarea
                            id="styleText"
                            value={formData.styleText}
                            onChange={e => setFormData(prev => ({ ...prev, styleText: e.target.value }))}
                            placeholder="Beschrijf je smaak, favoriete stijlen of wat je zoekt in een kringloop..."
                            className="min-h-[100px]"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="lookingForText">{t("social.style.lookingFor")}</Label>
                        <Input
                            id="lookingForText"
                            value={formData.lookingForText}
                            onChange={e => setFormData(prev => ({ ...prev, lookingForText: e.target.value }))}
                            placeholder="Bijv. Vintage design, keramiek, jaren 70 kleding..."
                        />
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-sm font-medium flex items-center gap-2">
                            Social Links
                            <Info className="h-3 w-3 text-muted-foreground" />
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="relative">
                                <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    value={formData.socialLinks.website}
                                    onChange={e => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, website: e.target.value } }))}
                                    placeholder="Website"
                                />
                            </div>
                            <div className="relative">
                                <Instagram className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    value={formData.socialLinks.instagram}
                                    onChange={e => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, instagram: e.target.value } }))}
                                    placeholder="Instagram"
                                />
                            </div>
                            <div className="relative">
                                <Pin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    value={formData.socialLinks.pinterest}
                                    onChange={e => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, pinterest: e.target.value } }))}
                                    placeholder="Pinterest"
                                />
                            </div>
                            <div className="relative">
                                <Facebook className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    value={formData.socialLinks.facebook}
                                    onChange={e => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, facebook: e.target.value } }))}
                                    placeholder="Facebook"
                                />
                            </div>
                            <div className="relative">
                                <Youtube className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    value={formData.socialLinks.youtube}
                                    onChange={e => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, youtube: e.target.value } }))}
                                    placeholder="YouTube"
                                />
                            </div>
                            <div className="relative">
                                <Music2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    value={formData.socialLinks.tiktok}
                                    onChange={e => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, tiktok: e.target.value } }))}
                                    placeholder="TikTok"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                        <div className="space-y-0.5">
                            <Label className="text-base">{t("social.wishlist.public")}</Label>
                            <p className="text-sm text-muted-foreground">
                                Maak je schattenverlanglijst zichtbaar voor andere schatzoekers.
                            </p>
                        </div>
                        <Switch
                            checked={formData.isWishlistPublic}
                            onCheckedChange={checked => setFormData(prev => ({ ...prev, isWishlistPublic: checked }))}
                        />
                    </div>

                    <Button type="submit" className="w-full" disabled={mutation.isPending}>
                        {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Profiel opslaan
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}
